import { DEFAULT_EMBED_VIDEO_URL, getVideoEmbedInfo } from '@/utils/mediaEmbed'
import { sanitizeBrandTheme } from '@/utils/themeOverrides'

const DEFAULT_HEADER_COLOR = '#FFFFFF'
const DEFAULT_HEADER_TEXT_COLOR = '#0F172A'
const DEFAULT_HEADER_HEIGHT_PX = 105
const MIN_HEADER_HEIGHT_PX = 72
const MAX_HEADER_HEIGHT_PX = 180

export const CUSTOMIZATION_API_PATHS = {
  get: '/scripts/profile/customization/get_customization.php',
  update: '/scripts/profile/customization/update_customization.php',
  updateOffice: '/scripts/profile/customization/update_office_customization.php',
  delete: '/scripts/profile/customization/delete_customization.php',
  uploadImage: '/scripts/profile/customization/upload_customization_img.php',
}

const BRANDING_API_ORIGIN =
  import.meta.env.VITE_BRANDING_API_ORIGIN || 'https://beta.stonewoodfinancial.com'

export function parseJsonObject(value) {
  if (!value) return {}
  if (typeof value === 'object' && !Array.isArray(value)) return value
  if (typeof value !== 'string') return {}

  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function parseJsonValue(value) {
  if (!value) return value
  if (typeof value !== 'string') return value

  const trimmedValue = value.trim()
  if (!trimmedValue) return value

  try {
    return JSON.parse(trimmedValue)
  } catch {
    const objectStart = trimmedValue.indexOf('{')
    const arrayStart = trimmedValue.indexOf('[')
    const jsonStart = [objectStart, arrayStart]
      .filter((index) => index >= 0)
      .sort((left, right) => left - right)[0] ?? -1
    const jsonEnd = Math.max(trimmedValue.lastIndexOf('}'), trimmedValue.lastIndexOf(']'))

    if (jsonStart >= 0 && jsonEnd > jsonStart) {
      try {
        return JSON.parse(trimmedValue.slice(jsonStart, jsonEnd + 1))
      } catch {
        return value
      }
    }
  }

  return value
}

export function normalizeBoolean(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['1', 'true', 'yes', 'y'].includes(normalized)) return true
    if (['0', 'false', 'no', 'n'].includes(normalized)) return false
  }
  return null
}

function normalizeNullableEmailValue(value) {
  if (value === null || value === undefined) return ''

  const normalizedValue = String(value).trim()
  if (!normalizedValue) return ''

  const textValue = normalizedValue
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
    .toLowerCase()

  return textValue === 'null' ? '' : String(value)
}

function normalizeVideoSource(value) {
  const source = String(value || '').trim().toLowerCase()
  return ['youtube', 'vimeo'].includes(source) ? source : ''
}

function normalizeHeaderColor(value) {
  const normalized = String(value || '').trim()
  return /^#[0-9A-Fa-f]{6}$/.test(normalized) ? normalized : DEFAULT_HEADER_COLOR
}

function normalizeHeaderTextColor(value) {
  const normalized = String(value || '').trim()
  return /^#[0-9A-Fa-f]{6}$/.test(normalized) ? normalized : DEFAULT_HEADER_TEXT_COLOR
}

function normalizeHeaderHeightPx(value) {
  const parsed = parseInt(String(value), 10)
  if (!Number.isFinite(parsed)) return DEFAULT_HEADER_HEIGHT_PX

  return Math.max(MIN_HEADER_HEIGHT_PX, Math.min(MAX_HEADER_HEIGHT_PX, parsed))
}

function normalizeMedia(rawMedia) {
  const media = parseJsonObject(rawMedia)
  const backgroundImage = parseJsonObject(media.backgroundImage)
  const video = parseJsonObject(media.video)
  const header = parseJsonObject(media.header)
  const normalized = {}
  const backgroundUrl = String(backgroundImage.url || media.heroImageUrl || '').trim()

  if (backgroundUrl) {
    normalized.heroImageUrl = backgroundUrl
  }

  const rawOverlayColor = String(backgroundImage.overlayColor || '').trim()
  if (/^#[0-9A-Fa-f]{6}$/.test(rawOverlayColor)) {
    normalized.overlayColor = rawOverlayColor
  }

  const rawOverlayOpacity = backgroundImage.overlayOpacity
  if (rawOverlayOpacity !== null && rawOverlayOpacity !== undefined) {
    const parsedOpacity = parseInt(String(rawOverlayOpacity), 10)
    if (!isNaN(parsedOpacity) && parsedOpacity >= 0 && parsedOpacity <= 100) {
      normalized.overlayOpacity = parsedOpacity
    }
  }

  const videoUrl = String(video.url || video.videoUrl || media.videoUrl || '').trim()
  const source = normalizeVideoSource(video.source || video.provider || video.videoSource || media.videoSource)

  if (videoUrl) {
    const selectedSource = source || undefined
    const info = getVideoEmbedInfo(videoUrl, selectedSource)
    if (info.isValid) {
      normalized.videoUrl = videoUrl
      normalized.videoSource = source || info.provider || 'vimeo'
    }
  } else if (videoUrl === '' && media.videoUrl === '') {
    normalized.videoUrl = DEFAULT_EMBED_VIDEO_URL
    normalized.videoSource = 'vimeo'
  }

  const rawHeaderColor = header.color || media.headerColor || media.header_color || ''
  const rawHeaderTextColor =
    header.textColor ||
    header.text_color ||
    media.headerTextColor ||
    media.header_text_color ||
    ''
  const rawHeaderHeight =
    header.heightPx ??
    header.height ??
    media.headerHeightPx ??
    media.header_height_px ??
    media.headerHeight

  normalized.header = {
    color: normalizeHeaderColor(rawHeaderColor),
    textColor: normalizeHeaderTextColor(rawHeaderTextColor),
    heightPx: normalizeHeaderHeightPx(rawHeaderHeight),
  }

  return normalized
}

export function normalizeCustomizationFromBranding(data) {
  const theme = sanitizeBrandTheme(parseJsonObject(data?.theme))
  const phoneRequired = normalizeBoolean(data?.phone_required)
  const attachPdf = normalizeBoolean(data?.attach_pdf)
  const emailSubject = normalizeNullableEmailValue(data?.email_subject)
  const emailBody = normalizeNullableEmailValue(data?.email_body)
  const payload = {
    id: data?.customization_id ?? null,
    name: data?.customization_name || '',
    state: data?.state || '',
    theme,
    media: normalizeMedia(data?.media),
    formOptions: {},
    email: {},
  }

  if (phoneRequired !== null) {
    payload.formOptions.phoneRequired = phoneRequired
  }

  if (emailSubject) {
    payload.email.subject = emailSubject
  }

  if (emailBody) {
    payload.email.bodyHtml = emailBody
  }

  if (attachPdf !== null) {
    payload.email.attachPdfReport = attachPdf
  }

  return payload
}

function getCustomizationApiUrl(path, searchParams = {}) {
  const params = new URLSearchParams()

  Object.entries(searchParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim()) {
      params.set(key, String(value).trim())
    }
  })

  const queryString = params.toString()
  const requestPath = queryString ? `${path}?${queryString}` : path

  if (import.meta.env.DEV) return requestPath

  return `${BRANDING_API_ORIGIN.replace(/\/$/, '')}${requestPath}`
}

export function buildListCustomizationsUrl(officeId, sessionId = '') {
  return getCustomizationApiUrl(CUSTOMIZATION_API_PATHS.get, {
    office_id: officeId,
    session_id: sessionId,
  })
}

export function buildUpdateOfficeCustomizationUrl(sessionId = '', authorId = '') {
  return getCustomizationApiUrl(CUSTOMIZATION_API_PATHS.updateOffice, {
    session_id: sessionId,
    u: authorId,
  })
}

export function buildUploadCustomizationImageUrl(sessionId = '') {
  return getCustomizationApiUrl(CUSTOMIZATION_API_PATHS.uploadImage, {
    session_id: sessionId,
  })
}

function getCustomizationArray(data) {
  const parsedData = parseJsonValue(data)

  if (Array.isArray(parsedData)) return parsedData
  if (!parsedData || typeof parsedData !== 'object') return []

  if (parsedData.success === false || parsedData.customization === null) return []

  const arrayFields = [
    parsedData.customizations,
    parsedData.customization,
    parsedData.records,
    parsedData.rows,
    parsedData.items,
    parsedData.results,
    parsedData.data,
  ]

  const matchedArray = arrayFields.find(Array.isArray)
  if (matchedArray) return matchedArray

  if (parsedData.data && typeof parsedData.data === 'object') {
    return getCustomizationArray(parsedData.data)
  }

  return Object.keys(parsedData).length ? [parsedData] : []
}

function getRecordPayload(record) {
  const parsedRecord = parseJsonValue(record)
  if (!parsedRecord || typeof parsedRecord !== 'object' || Array.isArray(parsedRecord)) return {}

  const payload = parseJsonObject(
    parsedRecord.payload ||
    parsedRecord.customization_payload ||
    parsedRecord.customization_json ||
    parsedRecord.configuration ||
    parsedRecord.config ||
    parsedRecord.json
  )

  return {
    ...payload,
    ...parsedRecord,
  }
}

export function normalizeCustomizationRecord(record, index = 0) {
  const source = getRecordPayload(record)
  const id = source.id ?? source.customization_id ?? source.customizationId ?? source.record_id ?? null
  const name = source.name || source.customization_name || source.title || `Customization ${index + 1}`

  return normalizeCustomizationFromBranding({
    ...source,
    customization_id: id,
    customization_name: name,
  })
}

export function normalizeCustomizationListResponse(data) {
  return getCustomizationArray(data)
    .map((record, index) => {
      const source = getRecordPayload(record)
      const normalized = normalizeCustomizationRecord(source, index)
      const id = normalized.id ?? source.id ?? source.customization_id ?? `customization-${index}`
      const customizationName = normalized.name || source.customization_name || source.name || `Customization ${index + 1}`

      return {
        id,
        name: customizationName,
        customizationName,
        state: normalized.state || '',
        createdDate: source.created_date || source.created_at || source.createdAt || source.date_created || '',
        createdByName: source.created_by_name || source.created_by || source.createdBy || '',
        updatedDate: source.updated_date || source.updated_at || source.updatedAt || source.date_updated || source.modified_at || '',
        updatedByName: source.updated_by_name || source.updated_by || source.updatedBy || '',
        raw: source,
        payload: normalized,
      }
    })
    .filter((record) => record.raw && Object.keys(record.raw).length)
}

export function buildGetCustomizationUrl(baseUrl, id, sessionId = '') {
  const url = new URL(CUSTOMIZATION_API_PATHS.get, baseUrl)
  url.searchParams.set('id', id)
  if (sessionId) url.searchParams.set('session_id', sessionId)
  return url.toString()
}

export function serializeCustomizationPayload(customization) {
  const rawHeader = parseJsonObject(customization?.media?.header)
  const headerColor = normalizeHeaderColor(rawHeader.color)
  const headerTextColor = normalizeHeaderTextColor(rawHeader.textColor)
  const headerHeightPx = normalizeHeaderHeightPx(rawHeader.heightPx)

  return {
    id: customization?.id ?? null,
    name: customization?.name || '',
    state: customization?.state || '',
    theme: JSON.stringify(sanitizeBrandTheme(customization?.theme || {})),
    media: JSON.stringify({
      video: {
        source: customization?.media?.videoSource || '',
        url: customization?.media?.videoUrl || '',
      },
      backgroundImage: {
        url: customization?.media?.heroImageUrl || '',
      },
      header: {
        color: headerColor,
        textColor: headerTextColor,
        heightPx: headerHeightPx,
      },
    }),
    phone_required: customization?.formOptions?.phoneRequired ? 1 : 0,
    email_subject: customization?.email?.subject || '',
    email_body: customization?.email?.bodyHtml || '',
    attach_pdf: customization?.email?.attachPdfReport ? 1 : 0,
  }
}

/**
 * Resolves all merge tag tokens in an email body HTML string by substituting
 * them with the real values from the store. A simple global string replacement
 * handles tokens in plain text, href= attributes, and src= attributes alike.
 *
 * @param {string} bodyHtml - The raw email body HTML containing tokens.
 * @param {object} store    - The Pinia store instance (or any object with .user / .company).
 * @param {object} [overrides] - Optional flat map of token → value to override store values.
 *                               Useful for generating previews with sample visitor data.
 */
export function resolveEmailBody(bodyHtml, store, overrides = {}) {
  if (!bodyHtml) return ''

  const replacements = {
    '[FirstName]': store.user?.first_name || '',
    '[LastName]': store.user?.last_name || '',
    '[CompanyName]': store.company?.company_name || '',
    '[CompanyWebsite]': store.company?.company_website || '',
    '[DisclousureEmail]': store.company?.lead_capture_disclosure || '',
    '[AdvisorName]': store.company?.userFullName || '',
    '[AdvisorPhone]': store.company?.phone || '',
    '[AdvisorEmail]': store.company?.advisor_email || '',
    '[AdvisorLeadCaptureMeetUrl]': store.company?.meeting_url || '',
    ...overrides,
  }

  return Object.entries(replacements).reduce((html, [token, value]) => {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return html.replace(new RegExp(escaped, 'g'), value)
  }, bodyHtml)
}

export function getSavedCustomizationId(data) {
  const parsedData = parseJsonValue(data)
  if (!parsedData || typeof parsedData !== 'object' || Array.isArray(parsedData)) return null

  return parsedData.id ||
    parsedData.customization_id ||
    parsedData.customizationId ||
    parsedData.record_id ||
    parsedData.data?.id ||
    parsedData.data?.customization_id ||
    parsedData.data?.customizationId ||
    parsedData.customization?.id ||
    parsedData.customization?.customization_id ||
    null
}
